import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import type { ICategoryCategory, IPerson, IStatistics } from '@/Module/Auth/Model/DataModel';
import {
  authGetCategoryByGradeIdAction,
  authGetMyPersonAction,
  authLoginVerifyOtpCodeAction,
  authSendOtpCodeAction,
  authUpdatePersonAction,
  getStatisticsAction,
} from '@/Module/Auth/Store/Action';

const AuthSlice = createSlice({
  name: 'auth',
  initialState: {
    isLoggedIn: false,
    isLoading: false,
    categoryCategory: null as ICategoryCategory | null,
    person: null as IPerson | null,
    watchVideoNeedLoginModalIsOpen: false,
    watchVideoNeedSubscriptionModalIsOpen: false,
    loginModalIsOpen: false,
    phoneNumber: null as string | null,
    parentId: null as number | null,
    rubikaToken: '',
    shadToken: '',
    isTransitioning: false,
    statistics: null as IStatistics | null,
    section: null as number | null,
  },
  reducers: {
    setLoginModalIsOpen: (state, { payload }: PayloadAction<boolean>) => {
      state.loginModalIsOpen = payload;
    },
    setSection: (state, { payload }: PayloadAction<number>) => {
      state.section = payload;
    },
    setPhoneNumber: (state, { payload }: PayloadAction<string>) => {
      state.phoneNumber = payload;
    },
    setLoggedIn: (state, { payload }: PayloadAction<boolean>) => {
      state.isLoggedIn = payload;

      if (!payload) state.person = null;
    },
    setRubikaToken: (state, { payload }: PayloadAction<string>) => {
      state.rubikaToken = payload;
    },
    setShadToken: (state, { payload }: PayloadAction<string>) => {
      state.shadToken = payload;
    },
    setIsTransitioning: (state, { payload }: PayloadAction<boolean>) => {
      state.isTransitioning = payload;
    },
    setVideoLockModal: (state, { payload }: PayloadAction<boolean>) => {
      state.watchVideoNeedLoginModalIsOpen = payload;
    },
    setVideoSubscriptionLockModal: (state, { payload }: PayloadAction<boolean>) => {
      state.watchVideoNeedSubscriptionModalIsOpen = payload;
    },
    setCategoryCategoryParentId: (state, { payload }: PayloadAction<number>) => {
      state.parentId = payload;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(authSendOtpCodeAction.pending, state => {
        state.isLoading = true;
      })
      .addCase(authSendOtpCodeAction.rejected, state => {
        state.isLoading = false;
      })
      .addCase(authSendOtpCodeAction.fulfilled, state => {
        state.isLoading = false;
      });

    builder
      .addCase(authLoginVerifyOtpCodeAction.pending, state => {
        state.isLoading = true;
      })
      .addCase(authLoginVerifyOtpCodeAction.rejected, state => {
        state.isLoading = false;
      })
      .addCase(authLoginVerifyOtpCodeAction.fulfilled, state => {
        state.isLoading = false;
      });

    builder
      .addCase(authGetMyPersonAction.pending, state => {
        state.isLoading = true;
      })
      .addCase(authGetMyPersonAction.rejected, state => {
        state.isLoading = false;
      })
      .addCase(authGetMyPersonAction.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.person = payload;
      });

    builder
      .addCase(authUpdatePersonAction.pending, state => {
        state.isLoading = true;
      })
      .addCase(authUpdatePersonAction.rejected, state => {
        state.isLoading = false;
      })
      .addCase(authUpdatePersonAction.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.person = payload;
      });

    builder
      .addCase(authGetCategoryByGradeIdAction.pending, state => {
        state.isLoading = true;
      })
      .addCase(authGetCategoryByGradeIdAction.rejected, state => {
        state.isLoading = false;
      })
      .addCase(authGetCategoryByGradeIdAction.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.categoryCategory = payload[0]!;
        state.parentId = payload[0].parentId || 3;
      });

    builder
      .addCase(getStatisticsAction.pending, state => {
        state.isLoading = true;
      })
      .addCase(getStatisticsAction.rejected, state => {
        state.isLoading = false;
      })
      .addCase(getStatisticsAction.fulfilled, (state, { payload }) => {
        state.statistics = payload;
      });
  },
});

export const {
  setLoggedIn: authSetLoggedInAction,
  setRubikaToken: authSetRubikaTokenAction,
  setShadToken: authSetShadTokenAction,
  setIsTransitioning: authSetIsTransitioningAction,
  setPhoneNumber: authSetPhoneNumber,
  setLoginModalIsOpen,
  setVideoLockModal: showVideoLoginModal,
  setVideoSubscriptionLockModal: showVideoSubscriptionModal,
  setCategoryCategoryParentId: categoryCategoryParentId,
  setSection: setSectionWelcome,
} = AuthSlice.actions;

export default AuthSlice;
